class CustomCondolenceMessages extends HTMLElement {
    constructor() {
        super();

        this.messages = [];

        this.totalPages = 0;
        this.currentPage = 1;

        this.masterCondolenceTab = document.querySelector('.condolences-tab');

        this.container = this.masterCondolenceTab.querySelector('.condolence-messages');
        this.countSpan = this.masterCondolenceTab.querySelector('.condolence-count');
        this.listEl = this.container.querySelector('.condolence-list');
        this.paginationEl = this.container.querySelector('.condolence-pagination');

        this.perPage = parseInt(this.paginationEl.dataset.perPage, 10) || 3;

        const raw = this.querySelector('script[type="application/ld+json"]').textContent;
        this.messages = JSON.parse(raw);

        // Set up visibility observer
        this.setupVisibilityObserver();
    }

    setupVisibilityObserver() {
        // Only set up if we have a parent tab to observe
        if (!this.masterCondolenceTab) {
            console.warn('No condolences-tab found to observe');
            return;
        }

        // Create the IntersectionObserver
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // When the tab becomes visible
                if (entry.isIntersecting) {
                    console.log('Condolence tab is now visible');
                    // Reset to first page and update read more buttons
                    this.showPage(1);
                }
            });
        }, {
            root: null, // viewport
            threshold: 0.1 // trigger when at least 10% is visible
        });

        // Start observing the tab
        this.observer.observe(this.masterCondolenceTab);
    }

    connectedCallback() {
        this.totalPages = Math.ceil(this.messages.length / this.perPage);

        if (this.messages.length > 0) {
            this.countSpan.textContent = `${this.messages.length} ${window.themeVariables.strings.condolence}`;
        }
        else {
            this.countSpan.style.display = 'none';
        }
        
        // Wait for DOM to be updated
        setTimeout(() => {
            this.renderAllMessages();
            this.showPage(this.currentPage);
        }, 0);
    }

    renderAllMessages() {
        this.listEl.classList.add('loading');
        this.listEl.innerHTML = '';

        // Create a container for each page
        for (let page = 1; page <= this.totalPages; page++) {
            const pageContainer = document.createElement('div');
            pageContainer.className = 'page-container tw-flex tw-flex-col tw-gap-4';
            pageContainer.dataset.page = page;
            // pageContainer.style.display = 'none'; // Hide all pages initially
            
            const start = (page - 1) * this.perPage;
            const pageMessages = this.messages.slice(start, start + this.perPage);

            for (const message of pageMessages) {
                const card = document.createElement('div');
                card.className = 'condolence-card tw-w-full tw-relative tw-p-4 tw-rounded-lg';
                card.style = `background: rgb(var(--color-background-secondary));`;
            
                // Create message paragraph with unique ID
                const messageId = `message-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
                
                card.innerHTML = `
                    <p id="${messageId}" class="message-truncated tw-text-gray-800 tw-mb-4 tw-whitespace-pre-line tw-text-lg tw-mb-2">${message.message}</p>
                    <div class="tw-flex tw-flex-col-reverse md:tw-flex-row tw-gap-2 tw-justify-between tw-items-center tw-mt-4">
                    <p class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-0"><strong>${message.prenom} ${message.nom}</strong></p>
                    <span class="read-more-btn tw-text-sm tw-underline link" data-message-id="${messageId}">${window.themeVariables.strings.readMore}</span>
                    </div>
                `;
                
                pageContainer.appendChild(card);
                
                // Store reference to check overflow later
                card.messageId = messageId;
            }
            
            this.listEl.appendChild(pageContainer);
        }
        
        // Add event listeners for read more buttons
        this.setupReadMoreListeners();

        this.renderPagination();
        this.listEl.classList.remove('loading');
    }
    
    setupReadMoreListeners() {
        document.querySelectorAll('.read-more-btn').forEach(btn => {
            // Remove any existing listeners to prevent duplicates
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);
            
            newBtn.addEventListener('click', (e) => {
                const messageId = newBtn.getAttribute('data-message-id');
                const messageEl = document.getElementById(messageId);
                
                if (!messageEl) return;
                
                if (messageEl.style.webkitLineClamp === 'none' || 
                    messageEl.style.display === 'block') {
                    // Collapse the message
                    messageEl.style.webkitLineClamp = '';
                    messageEl.style.display = '';
                    messageEl.classList.add('message-truncated');
                    newBtn.textContent = window.themeVariables.strings.readMore;
                    
                    // Re-check if it's actually truncated
                    setTimeout(() => this.checkIfTruncated(messageEl), 10);
                } else {
                    // Expand the message
                    messageEl.style.webkitLineClamp = 'none';
                    messageEl.style.display = 'block';
                    messageEl.classList.remove('message-truncated');
                    newBtn.textContent = window.themeVariables.strings.readLess;
                }
                
                e.stopPropagation();
            });
        });
    }
    
    // Check if text is truncated by comparing original height with a clone's height
    checkIfTruncated(element) {
        if (!element) return false;
        
        // Create a clone to measure full height
        const clone = element.cloneNode(true);
        clone.classList.remove('message-truncated');
        clone.style.visibility = "hidden";
        clone.style.position = "absolute";
        clone.style.display = "block";
        clone.style.webkitLineClamp = "none";
        clone.style.width = element.offsetWidth + "px"; // Match the width
        document.body.appendChild(clone);
        
        // Compare heights
        const originalHeight = element.offsetHeight;
        const fullHeight = clone.offsetHeight;
        const isTruncated = fullHeight > originalHeight;
        
        // Clean up
        document.body.removeChild(clone);
        
        // Add class if truncated
        if (isTruncated) {
            element.classList.add('is-truncated');
        } else {
            element.classList.remove('is-truncated');
        }
        
        return isTruncated;
    }
    
    updateReadMoreButtons() {
        // First make sure the container is visible for accurate measurements
        const visiblePage = document.querySelector('.page-container.active');
        if (!visiblePage) return;
        
        // Find all messages in the visible page
        visiblePage.querySelectorAll('.message-truncated').forEach(messageEl => {
            this.checkIfTruncated(messageEl);
        });
    }
    
    showPage(pageNumber) {
        // Hide all pages
        document.querySelectorAll('.page-container').forEach(page => {
            // page.style.display = 'none';
            page.classList.remove('active');
        });
        
        // Show the selected page
        const selectedPage = document.querySelector(`.page-container[data-page="${pageNumber}"]`);
        if (selectedPage) {
            selectedPage.classList.add('active');
            // selectedPage.style.display = 'flex';
        }
        
        // Update current page
        this.currentPage = pageNumber;
        
        // Update pagination buttons
        this.updatePaginationButtons();
        
        // Update read more buttons with a delay to ensure the page is visible
        setTimeout(() => this.updateReadMoreButtons(), 100);
    }
    
    updatePaginationButtons() {
        // Update current page button
        const currentPageBtn = this.paginationEl.querySelector('.current-child');
        if (currentPageBtn) {
            currentPageBtn.textContent = this.currentPage;
        }
        
        // Update previous button state
        const prevBtn = this.paginationEl.querySelector('button:first-child');
        if (prevBtn) {
            prevBtn.disabled = this.currentPage === 1;
        }
        
        // Update next button state
        const nextBtn = this.paginationEl.querySelector('button:last-child');
        if (nextBtn) {
            nextBtn.disabled = this.currentPage === this.totalPages;
        }
    }

    renderPagination() {
        this.paginationEl.innerHTML = '';

        const createButton = (label, disabled, onClick, isActive = false) => {
            const btn = document.createElement('button');
            btn.innerHTML = label;
            if (disabled) btn.disabled = true;
            if (isActive) btn.classList.add('active');
            btn.onclick = onClick;
            return btn;
        };

        if(this.totalPages > 1) {
            // Previous button
            this.paginationEl.appendChild(
                createButton(
                    `<svg width="16" height="16" style="transform: rotate(180deg)" viewBox="0 0 16 16" class="icon icon-caret" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>`,
                    this.currentPage === 1,
                    () => {
                        if (this.currentPage > 1) {
                            this.showPage(this.currentPage - 1);
                        }
                    }
                )
            );

            // Current page button
            var currentPageChild = createButton(this.currentPage, false, () => {});
            currentPageChild.classList.add("current-child");
            this.paginationEl.appendChild(currentPageChild);

            // Next button
            this.paginationEl.appendChild(
                createButton(
                    `<svg width="16" height="16" viewBox="0 0 16 16" class="icon icon-caret" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>`, 
                    this.currentPage === this.totalPages, 
                    () => {
                        if (this.currentPage < this.totalPages) {
                            this.showPage(this.currentPage + 1);
                        }
                    }
                )
            );
        }
        else this.paginationEl.classList.add('tw-hidden');
    }
    
    // Clean up when element is removed
    disconnectedCallback() {
        // Remove the intersection observer if it exists
        if (this.observer && this.masterCondolenceTab) {
            this.observer.unobserve(this.masterCondolenceTab);
            this.observer.disconnect();
        }
    }
}

if (!window.customElements.get('condolence-messages')) {
    window.customElements.define('condolence-messages', CustomCondolenceMessages);
}
